// Construction queue — the classic "wait for the works to finish" loop.
// Building a court, raising a building or upgrading it no longer happens
// instantly: you pay up front, a job sits in the queue for a number of
// in-game days, and the structure materialises when the works end (driven
// by ConstructionSystem on DAY_CHANGED). Durations are in IN-GAME DAYS, so
// they scale naturally with the game speed (~60s/day at 1x).

import type { BuildingType } from './buildingConfig'

export type ConstructionKind = 'court' | 'building' | 'building_upgrade' | 'court_upgrade'

export interface ConstructionJob {
  id: string
  kind: ConstructionKind
  /** Human label shown in the timer + reveal, e.g. "Pista Outdoor", "Cafetería Nv.2" */
  label: string
  startDay: number
  endDay: number
  totalDays: number
  /** Facility visual id used for the reveal image ('court' | building type) */
  visualId: string
  /** Tier to reveal once finished */
  tier: number
  // --- materialisation payload (by kind) ---
  courtType?: 'outdoor' | 'indoor'
  pricePerHour?: number
  buildingType?: BuildingType
  buildingId?: string
  targetLevel?: number
  // court_upgrade: which court is being improved and with what upgrade
  courtId?: string
  courtUpgradeId?: string
}

/** What the player needs to start a job; the store stamps id/start/end. */
export type ConstructionInput = Omit<ConstructionJob, 'id' | 'startDay' | 'endDay'> & {
  cost: number
}

/** A finished work, kept briefly for the campus "postcards" strip */
export interface CompletedWork {
  visualId: string
  label: string
  tier: number
  kind: ConstructionKind
  /** In-game day the works finished */
  day: number
}

/** How many finished works the campus strip remembers */
export const RECENT_COMPLETIONS_LIMIT = 4

// How long each kind of works takes, in in-game days.
export const CONSTRUCTION_DAYS = {
  courtOutdoor: 3,
  courtIndoor: 6,
  building: 4,
  // A building upgrade scales a little with the target level (Nv.2 = 3d, Nv.3 = 4d)
  buildingUpgradeBase: 2,
  // A court improvement closes the court for a couple of in-game days
  courtUpgrade: 2,
} as const

export function courtConstructionDays(type: 'outdoor' | 'indoor'): number {
  return type === 'outdoor' ? CONSTRUCTION_DAYS.courtOutdoor : CONSTRUCTION_DAYS.courtIndoor
}

export function courtUpgradeDays(): number {
  return CONSTRUCTION_DAYS.courtUpgrade
}

/** Days left until a job finishes, given the current day (never negative). */
export function daysRemaining(job: ConstructionJob, currentDay: number): number {
  return Math.max(0, job.endDay - currentDay)
}

/** 0..1 progress of a job, given the current day. */
export function constructionProgress(job: ConstructionJob, currentDay: number): number {
  if (job.totalDays <= 0) return 1
  const elapsed = currentDay - job.startDay
  return Math.max(0, Math.min(1, elapsed / job.totalDays))
}

/** Copy for the "works finished" reveal, by kind. */
export function constructionRevealCopy(job: ConstructionJob): { kicker: string; cta: string } {
  switch (job.kind) {
    case 'court':
      return { kicker: 'Pista lista', cta: 'Ver instalaciones' }
    case 'building':
      return { kicker: 'Edificio inaugurado', cta: 'Ver instalaciones' }
    case 'building_upgrade':
      return { kicker: 'Mejora terminada', cta: 'Ver instalaciones' }
    case 'court_upgrade':
      return { kicker: 'Pista mejorada', cta: 'Ver instalaciones' }
  }
}
